import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import SVG from 'react-inlinesvg';
import { DateTime } from 'luxon';
import { Button } from 'shared-assets/components/Elements';
import DatePicker from 'shared-assets/components/DatePicker';
import colors from 'shared-assets/lib/colors';

const DatePickerContainer = styled.div`
  > * {
    display: inline-block;
  }
  > *:only-child {
    display: block;
  }
  input {
    font-size: 1rem;
    height: 2.25rem;
    border: 1px solid ${colors.accents.border};
    outline: 0;
    padding: 5px 7px;
    border-radius: 3px;
    box-shadow: inset 0 1px 3px ${colors.accents.shadow};
    padding-right: 7px;
  }
`;

const CalendarButton = styled.button`
  border: none;
  padding: 0px;
  background-color: transparent;
  height: 2rem;
  line-height: 2em !important;
  margin-right: 5px !important;
  vertical-align: middle !important;
  outline: none;
`;

class CustomDatePickerRange extends React.PureComponent {
  constructor(props) {
    super(props);

    let startDateProps = this.props.defaultValues.startDate
      ? this.props.defaultValues.startDate
      : '';
    let endDateProps = this.props.defaultValues.endDate
      ? this.props.defaultValues.endDate
      : '';

    this.state = {
      startDate: startDateProps,
      endDate: endDateProps,
      startDateInputError: false,
      endDateInputError: false,
      lastStartDateValid: true,
      lastEndDateValid: true,
      selectedPeriod: '', // showSelectionShortcuts backwards compatibility
      startDateTouched: false,
      endDateTouched: false,
    };

    this.today = DateTime.fromJSDate(new Date(), {
      zone: 'local',
    }).toISODate();
    this.yearOptions = [];
    let currentYear = DateTime.fromISO(this.today).year;
    for (
      let i = currentYear + this.props.numFutureYears;
      i > currentYear;
      i--
    ) {
      this.yearOptions.push(i);
    }

    for (let i = currentYear; i >= currentYear - this.props.numPastYears; i--) {
      this.yearOptions.push(i);
    }
  }

  allowedFormats = [
    'LL-dd-yyyy',
    'LL-d-yyyy',
    'L-d-yyyy',
    'L-d-yy',
    'L-dd-yyyy',
    'L-dd-yy',
    'LL-d-yy',
    'LL-dd-yy',
    'L/d/yyyy',
    'L/d/yy',
    'L/dd/yyyy',
    'L/dd/yy',
    'LL/d/yyyy',
    'LL/d/yy',
    'LL/dd/yyyy',
    'LL/dd/yy',
    'LLddyyyy',
    'Lddyyyy',
    'LLdyyyy',
    'LLddyy',
    'Ldyyyy',
    'Lddyy',
    'LLdyy',
    'Ldyy',
  ];

  actionClicked = e => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    if (this.props.clearOnActionButtonClick) {
      this.setState({
        startDate: '',
        endDate: '',
        startDateInputError: false,
        endDateInputError: false,
        lastStartDateValid: true,
        lastEndDateValid: true,
      });
      this.startDateInput.value = '';
      this.endDateInput.value = '';
    }
    this.props.onActionButtonClick(e);
  };

  startDateChanged = startDate => {
    this.setState({
      startDate: DateTime.fromISO(startDate.iso),
      startDateInputError: false,
      lastStartDateValid: true,
      selectedPeriod: '',
    });

    this.props.onValidationError({
      startDateError: false,
    });

    if (this.state.lastEndDateValid) {
      this.validateDateRange(
        DateTime.fromISO(startDate.iso),
        this.state.endDate,
      );
    }

    this.startDateInput.value = DateTime.fromISO(startDate.iso).toFormat(
      'LL/dd/yyyy',
    );
    this.props.onStartDateChange(startDate.iso);
  };

  startDateInputChanged = (event, close, open) => {
    if (event.target.value === '') {
      this.setState({
        startDate: '',
        startDateInputError: false,
        lastStartDateValid: true,
        selectedPeriod: '',
      });
      this.startDateInput.value = '';
      this.props.onStartDateChange('');
      open();
    } else {
      close();
    }
  };

  startDateInputBlur = event => {
    if (!this.state.startDateTouched) {
      this.setState({ startDateTouched: true });
    }

    this.setState({
      selectedPeriod: '',
    });
    if (event.target.value !== '') {
      var dateValue = null;
      for (let i = 0; i < this.allowedFormats.length; i++) {
        dateValue = DateTime.fromFormat(
          event.target.value,
          this.allowedFormats[i],
        );
        if (dateValue.isValid) {
          break;
        }
      }

      if (dateValue.isValid && this.isDateOnYearRange(dateValue)) {
        var sameDateFlag =
          this.state.startDate === '' ||
          dateValue.toISODate() !== this.state.startDate.toISODate();

        this.setState({
          startDate: dateValue,
          startDateInputError: false,
          lastStartDateValid: true,
        });

        this.props.onValidationError({
          startDateError: false,
        });

        if (sameDateFlag) {
          this.props.onStartDateChange(dateValue.toISO());
        }

        event.target.value = dateValue.toFormat('LL/dd/yyyy');

        if (this.state.lastEndDateValid) {
          this.setState({
            endDateInputError: false,
          });

          this.props.onValidationError({
            endDateError: false,
          });

          this.validateDateRange(dateValue, this.state.endDate);
        }
      } else {
        this.setState({
          startDateInputError: true,
          lastStartDateValid: false,
        });

        this.props.onValidationError({
          startDateError: true,
        });

        if (this.state.lastEndDateValid) {
          this.setState({
            endDateInputError: false,
          });

          this.props.onValidationError({
            endDateError: false,
          });
        }
      }
    } else {
      const startDateError = this.props.errorIfStartDateEmpty || false;
      this.setState({
        startDateInputError: startDateError,
        lastStartDateValid: !this.props.errorIfStartDateEmpty,
      });

      this.props.onValidationError({ startDateError });

      if (this.props.fireChangeOnEmpty && this.state.startDate) {
        this.setState({
          startDate: '',
        });
        this.props.onStartDateChange(null);
      }
    }
  };

  endDateInputChanged = (event, close, open) => {
    if (event.target.value === '') {
      this.setState({
        endDate: '',
        endDateInputError: false,
        lastEndDateValid: true,
        selectedPeriod: '',
      });
      this.endDateInput.value = '';
      this.props.onEndDateChange('');
      open();
    } else {
      close();
    }
  };

  endDateInputBlur = event => {
    if (!this.state.endDateTouched) {
      this.setState({ endDateTouched: true });
    }
    this.setState({
      selectedPeriod: '',
    });
    if (event.target.value !== '') {
      var dateValue = null;
      for (let i = 0; i < this.allowedFormats.length; i++) {
        dateValue = DateTime.fromFormat(
          event.target.value,
          this.allowedFormats[i],
        );
        if (dateValue.isValid) {
          break;
        }
      }

      if (dateValue.isValid && this.isDateOnYearRange(dateValue)) {
        var sameDateFlag =
          this.state.endDate === '' ||
          dateValue.toISODate() !== this.state.endDate.toISODate();

        this.setState({
          endDate: dateValue,
          endDateInputError: false,
          lastEndDateValid: true,
        });

        this.props.onValidationError({
          endDateError: false,
        });

        if (sameDateFlag) {
          this.props.onEndDateChange(dateValue.toISO());
        }

        event.target.value = dateValue.toFormat('LL/dd/yyyy');

        if (this.state.lastStartDateValid) {
          this.setState({
            startDateInputError: false,
          });

          this.props.onValidationError({
            startDateError: false,
          });

          this.validateDateRange(this.state.startDate, dateValue);
        }
      } else {
        this.setState({
          endDateInputError: true,
          lastEndDateValid: false,
        });

        this.props.onValidationError({
          endDateError: true,
        });

        if (this.state.lastStartDateValid) {
          this.setState({
            startDateInputError: false,
          });

          this.props.onValidationError({
            startDateError: false,
          });
        }
      }
    } else {
      const endDateError = this.props.errorIfEndDateEmpty || false;
      this.setState({
        endDateInputError: endDateError,
        lastEndDateValid: !this.props.errorIfEndDateEmpty,
      });

      this.props.onValidationError({ endDateError });

      if (this.props.fireChangeOnEmpty && this.state.endDate) {
        this.setState({
          endDate: '',
        });
        this.props.onEndDateChange(null);
      }
    }
  };

  endDateChanged = endDate => {
    this.setState({
      endDate: DateTime.fromISO(endDate.iso),
      endDateInputError: false,
      lastEndDateValid: true,
      selectedPeriod: '',
    });

    this.props.onValidationError({
      endDateError: false,
    });

    if (this.state.lastStartDateValid) {
      this.validateDateRange(
        this.state.startDate,
        DateTime.fromISO(endDate.iso),
      );
    }

    this.endDateInput.value = DateTime.fromISO(endDate.iso).toFormat(
      'LL/dd/yyyy',
    );
    this.props.onEndDateChange(endDate.iso);
  };

  validateDateRange = (startDate, endDate) => {
    if (startDate !== '' && endDate !== '' && startDate > endDate) {
      this.setState({
        startDateInputError: true,
        endDateInputError: true,
      });
      this.props.onValidationError({ rangeError: true });
    } else {
      this.setState({
        startDateInputError: false,
        endDateInputError: false,
      });
      this.props.onValidationError({ rangeError: false });
    }
  };

  isDateOnYearRange = date => {
    if (this.props.yearLimitEnabled) {
      if (!this.yearOptions.includes(date.year)) {
        return false;
      }
    }
    return true;
  };

  closeOnTab = (event, close) => {
    if (event.keyCode === 9) {
      close();
    }
  };

  executeOnEnter = (event, dateToExecute) => {
    if (event.nativeEvent.keyCode === 13) {
      if (dateToExecute === 'start') {
        this.startDateInputBlur(event);
      } else if (dateToExecute === 'end') {
        this.endDateInputBlur(event);
      }
    }
  };

  selectPeriod = (duration, period) => {
    let periodLuxonDict = { y: 'year', M: 'month', d: 'days' };
    let endDatePeriod = DateTime.local();
    let startDatePeriod = endDatePeriod.minus({
      [periodLuxonDict[period]]: duration,
    });

    this.setState({
      startDate: startDatePeriod,
      endDate: endDatePeriod,
      startDateInputError: false,
      endDateInputError: false,
      lastStartDateValid: true,
      lastEndDateValid: true,
      selectedPeriod: duration + period,
    });

    this.startDateInput.value = startDatePeriod.toFormat('LL/dd/yyyy');

    this.endDateInput.value = endDatePeriod.toFormat('LL/dd/yyyy');

    this.props.onEndDateChange(endDatePeriod.toISO());
    this.props.onStartDateChange(startDatePeriod.toISO());
    this.props.onActionButtonClick();
  };

  render() {
    return (
      <DatePickerContainer style={{ ...this.props.style }}>
        {this.props.showSelectionShortcuts && (
          <div className="button-group left mrs ng-scope">
            <button
              className={
                this.state.selectedPeriod === '6d'
                  ? 'button buttonlink subtle active'
                  : 'button buttonlink subtle'
              }
              onClick={() => this.selectPeriod(6, 'd')}
            >
              1w
            </button>
            <button
              className={
                this.state.selectedPeriod === '1M'
                  ? 'button buttonlink subtle active'
                  : 'button buttonlink subtle'
              }
              onClick={() => this.selectPeriod(1, 'M')}
            >
              1m
            </button>
            <button
              className={
                this.state.selectedPeriod === '6M'
                  ? 'button buttonlink subtle active'
                  : 'button buttonlink subtle'
              }
              onClick={() => this.selectPeriod(6, 'M')}
            >
              6m
            </button>
            <button
              className={
                this.state.selectedPeriod === '1y'
                  ? 'button buttonlink subtle active'
                  : 'button buttonlink subtle'
              }
              onClick={() => this.selectPeriod(1, 'y')}
            >
              1y
            </button>
          </div>
        )}
        <DatePicker
          value={
            this.state.startDate !== '' ? this.state.startDate.toISO() : ''
          }
          onChange={this.startDateChanged}
          showMonthAndYearSelection={this.props.showMonthAndYearSelection}
          yearLimitEnabled={this.props.yearLimitEnabled}
          numFutureYears={this.props.numFutureYears}
          numPastYears={this.props.numPastYears}
        >
          {({ open, close }) => (
            <React.Fragment>
              <CalendarButton
                aria-label="Open Calendar"
                type="button"
                onClick={open}
                tabIndex="-1"
                disabled={this.props.disabledFirstInput}
              >
                <SVG src={require('shared-assets/assets/icons/calendar.svg')} />
              </CalendarButton>
              <input
                disabled={this.props.disabledFirstInput}
                ref={input => {
                  this.startDateInput = input;
                }}
                id="startDateInput"
                data-testid="start-date-input"
                defaultValue={
                  this.state.startDate !== ''
                    ? this.state.startDate.toFormat('LL/dd/yyyy')
                    : ''
                }
                autoComplete="off"
                onFocus={open}
                onClick={open}
                onBlur={event => {
                  this.startDateInputBlur(event);
                }}
                onChange={event => {
                  this.startDateInputChanged(event, close, open);
                }}
                onKeyDown={event => {
                  this.closeOnTab(event, close);
                }}
                onKeyPress={event => {
                  this.executeOnEnter(event, 'start');
                }}
                size="10"
                placeholder="mm/dd/yyyy"
                style={
                  this.state.startDateInputError ||
                  !this.state.lastStartDateValid
                    ? {
                        borderColor: colors.error,
                        width: this.props.inputWidth,
                      }
                    : {
                        width: this.props.inputWidth,
                      }
                }
              />
            </React.Fragment>
          )}
        </DatePicker>
        {!this.props.isSingleDate && (
          <React.Fragment>
            <label style={{ marginLeft: '5px', marginRight: '5px' }}>to</label>
            <DatePicker
              value={
                this.state.endDate !== '' ? this.state.endDate.toISO() : ''
              }
              onChange={this.endDateChanged}
              showMonthAndYearSelection={this.props.showMonthAndYearSelection}
              yearLimitEnabled={this.props.yearLimitEnabled}
              numFutureYears={this.props.numFutureYears}
              numPastYears={this.props.numPastYears}
            >
              {({ open, close }) => (
                <div>
                  <CalendarButton
                    aria-label="Open Calendar"
                    type="button"
                    onClick={open}
                    tabIndex="-1"
                    disabled={this.props.disabledSecondInput}
                  >
                    <SVG
                      src={require('shared-assets/assets/icons/calendar.svg')}
                    />
                  </CalendarButton>
                  <input
                    disabled={this.props.disabledSecondInput}
                    ref={input => {
                      this.endDateInput = input;
                    }}
                    id="endDateInput"
                    data-testid="end-date-input"
                    defaultValue={
                      this.state.endDate !== ''
                        ? this.state.endDate.toFormat('LL/dd/yyyy')
                        : ''
                    }
                    autoComplete="off"
                    onFocus={open}
                    onClick={open}
                    onBlur={event => {
                      this.endDateInputBlur(event);
                    }}
                    onChange={event => {
                      this.endDateInputChanged(event, close, open);
                    }}
                    onKeyDown={event => {
                      this.closeOnTab(event, close);
                    }}
                    onKeyPress={event => {
                      this.executeOnEnter(event, 'end');
                    }}
                    size="10"
                    placeholder="mm/dd/yyyy"
                    style={
                      this.state.endDateInputError ||
                      !this.state.lastEndDateValid
                        ? {
                            borderColor: colors.error,
                            width: this.props.inputWidth,
                          }
                        : {
                            width: this.props.inputWidth,
                          }
                    }
                  />
                </div>
              )}
            </DatePicker>
          </React.Fragment>
        )}
        {this.props.actionButtonEnabled && (
          <div className="prm">
            <Button
              type="button"
              className="link"
              onClick={e => this.actionClicked(e)}
              style={{ marginLeft: '5px' }}
              disabled={
                !this.props.clearOnActionButtonClick &&
                (this.state.endDateInputError ||
                  !this.state.lastEndDateValid ||
                  this.state.startDateInputError ||
                  !this.state.lastStartDateValid)
              }
            >
              {this.props.actionButtonLabel}
            </Button>
          </div>
        )}
      </DatePickerContainer>
    );
  }
}

CustomDatePickerRange.defaultProps = {
  onStartDateChange: () => {},
  onEndDateChange: () => {},
  onActionButtonClick: () => {},
  onValidationError: () => {},
  defaultValues: {
    startDate: undefined,
    endDate: undefined,
  },
  isSingleDate: false,
  actionButtonEnabled: true,
  actionButtonLabel: 'Update',
  clearOnActionButtonClick: false,
  showMonthAndYearSelection: false,
  yearLimitEnabled: false,
  showSelectionShortcuts: false,
  numFutureYears: 0,
  numPastYears: 10,
  inputWidth: '6.7rem',
  disabledFirstInput: false,
  disabledSecondInput: false,
  fireChangeOnEmpty: false,
  errorIfStartDateEmpty: false,
  errorIfEndDateEmpty: false,
  style: {},
};

CustomDatePickerRange.propTypes = {
  onStartDateChange: PropTypes.func,
  onEndDateChange: PropTypes.func,
  onActionButtonClick: PropTypes.func,
  onValidationError: PropTypes.func,
  defaultValues: PropTypes.shape({
    startDate: PropTypes.instanceOf(DateTime),
    endDate: PropTypes.instanceOf(DateTime),
  }),
  fireChangeOnEmpty: PropTypes.bool,
  errorIfStartDateEmpty: PropTypes.bool,
  errorIfEndDateEmpty: PropTypes.bool,
  isSingleDate: PropTypes.bool,
  actionButtonEnabled: PropTypes.bool,
  actionButtonLabel: PropTypes.string,
  clearOnActionButtonClick: PropTypes.bool,
  showMonthAndYearSelection: PropTypes.bool,
  yearLimitEnabled: PropTypes.bool,
  showSelectionShortcuts: PropTypes.bool,
  numFutureYears: PropTypes.number,
  numPastYears: PropTypes.number,
  inputWidth: PropTypes.string,
  disabledFirstInput: PropTypes.bool,
  disabledSecondInput: PropTypes.bool,
  style: PropTypes.object,
};

export default CustomDatePickerRange;
